/**
 * The router's authentication gate. Registered once by `router.ts`.
 */
import type { NavigationGuardWithThis, RouteLocationRaw } from 'vue-router'

import { useSession } from './session.js'

/**
 * Routes opt out with `meta.public` (login, 404). Everything else needs a
 * session, and the path the user actually asked for is carried in `redirect`
 * so signing in lands them where they were going instead of on the flow list.
 *
 * No `await session.restore()` here: `installAuth` has already resolved the
 * session before the router is installed, so this guard can decide
 * synchronously and never flashes the login page at an authenticated user.
 */
export const requireSession: NavigationGuardWithThis<undefined> = (
  to,
): RouteLocationRaw | boolean => {
  const session = useSession()

  if (to.meta.public === true) {
    // An authenticated user hitting /login has nothing to do there; bounce
    // them to the app rather than showing a form that would sign them in
    // again. The 404 route is public too, and must still render.
    if (session.isAuthenticated && to.name === 'login') {
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : null
      return redirect ?? { name: 'flows' }
    }
    return true
  }

  if (session.isAuthenticated) return true

  return { name: 'login', query: { redirect: to.fullPath } }
}
